import { Injectable } from '@angular/core';

export enum EventName {
    AccuracyHints = 'accuracy:hints',
    AssetDeleted = 'asset:deleted',
    TrackableJobsPolled = 'trackable-jobs:polled',
    TrackableJobCanceled = 'trackable-job:canceled',
    TrackableJobCanceling = 'trackable-job:canceling',
    TrackableJobFailed = 'trackable-job:failed',
    TrackableJobFinished = 'trackable-job:finished',
    TrackableJobProcessing = 'trackable-job:processing',
    TrackableJobUpdated = 'trackable-job:updated',
    TrackableJobWaiting = 'trackable-job:waiting',
    BoostRecalculating = 'boost:recalculating',
    CanvasClassSelection = 'canvas:classSelection',
    CanvasOverlappingPolygons = 'canvas:overlappingPolygons',
    CanvasPolygonRemoved = 'canvas:polygon:removed',
    CanvasPolygonsActivated = 'canvas:polygons:activated',
    CanvasZoomChanged = 'canvas:zoomChanged',
    CanvasSaveableChange = 'canvas:saveableChange',
    CanvasTrainableChange = 'canvas:trainableChange',
    ClassChanged = 'class:changed',
    CustomHotKeyHandler = 'customHotkeyHandler',
    DatasetChanged = 'dataset:changed',
    DatasetProviderDelete = 'datasetProvider:delete',
    DatasetProviderFetchDatasets = 'datasetProvider:fetchDatasets',
    DatasetReloaded = 'dataset:reloaded',
    DatasetSetEnableAnomalyHiFi = 'dataset:setEnableAnomalyHiFi',
    DatasetSetEnableStaticClassifier = 'dataset:setEnableStaticClassifier',
    DatasetSetLearnAsYouGo = 'dataset:setLearnAsYouGo',
    EvaluatePageSetImageDetails = 'evaluatePage:setImageDetails',
    ExportDone = 'export:done',
    ExportStarted = 'export:started',
    JobDismissed = 'job:dismissed',
    JobFinished = 'job:finished',
    NewDatasetName = 'newDatasetName',
    TestSetDeleted = 'test-set:deleted',
    TestSetUploadCompleted = 'test-set:upload-completed',
    TestSetUploading = 'test-set:uploading',
    TestSetUpdating = 'test-set:updating',
    TestSetUpdated = 'test-set:updated',
    ToggleDetectionOptionsModal = 'toggleDetectionOptionsModal',
    ToggleSegmentationOptionsPopover = 'toggleSegmentationOptionsPopover',
    TrainFinished = 'train:finished',
    UploadDone = 'upload:done',
    WorkSpaceSaveDone = 'workspace-save:done',
    WorkingImagesEmpty = 'workingImages:empty',
    WorkingImagesSet = 'workingImages:set',
    WorkspaceClassAssigned = 'workspace:classAssigned',
    WorkspaceSaveFailed = 'workspace-save:failed',
    WorkspaceSettingsUpdated = 'workspace-settings:updated'
}

/**
 * Manage Events in a way that is compatible with the Ionic 3 Events framework
 * In fact this code is pulled directly from the Ionic 3 repository
 * https://github.com/ionic-team/ionic-v3/blob/2b7292e8a1187351fff9629bcef271d277abb318/src/util/events.ts#L181
 */
@Injectable()
export class Events {
    private _channels: any = [];

    /**
     * Subscribe to an event topic. Events that get posted to that topic will trigger the provided handler.
     *
     * @param {string} topic the topic to subscribe to
     * @param {function} handler the event handler
     */
    subscribe(topic: string, ...handlers: Function[]) {
        if (!this._channels[topic]) {
            this._channels[topic] = [];
        }

        handlers.forEach(handler => {
            this._channels[topic].push(handler);
        });
    }

    /**
     * Unsubscribe from the given topic. Your handler will no longer receive events published to this topic.
     *
     * @param {string} topic the topic to unsubscribe from
     * @param {function} handler the event handler
     *
     * @return true if a handler was removed
     */
    unsubscribe(topic: string, handler: Function | null = null) {
        const t = this._channels[topic];
        if (!t) {
            // Wasn't found, wasn't removed
            return false;
        }

        if (!handler) {
            // Remove all handlers for this topic
            delete this._channels[topic];
            return true;
        }

        // We need to find and remove a specific handler
        const i = t.indexOf(handler);

        if (i < 0) {
            // Wasn't found, wasn't removed
            return false;
        }

        t.splice(i, 1);

        // If the channel is empty now, remove it from the channel map
        if (!t.length) {
            delete this._channels[topic];
        }

        return true;
    }

    /**
     * Publish an event to the given topic.
     *
     * @param {string} topic the topic to publish to
     * @param {any} eventData the data to send as the event
     */
    publish(topic: string, ...args: any[]) {
        const t = this._channels[topic];
        if (!t) {
            return null;
        }

        const responses: any[] = [];
        t.forEach((handler: any) => {
            responses.push(handler(...args));
        });

        return responses;
    }
}



// WEBPACK FOOTER //
// ./src/utils/events.ts